import React, { useMemo, useState } from 'react';
import { addableRowTypes, CHEKHOV_VISIBILITIES, chekhovVisibilityCopy } from '../../utils/plottrackGrid';
import {
  ADD_CHEKHOV,
  ADD_MAIN,
  ADD_SUBPLOT,
  ADD_THROUGH_LINE,
  CANCEL_THROUGH_LINE,
  CHEKHOV_FEEDS_SECRET,
  CHEKHOV_HOW_CLEAR,
  CHEKHOV_KIND,
  MAIN_KIND,
  NAME_THIS_THROUGH_LINE,
  SUBPLOT_KIND,
  THROUGH_LINE_NAME,
} from '../../utils/writerCopy';

const TYPE_COPY = {
  subplot: { label: SUBPLOT_KIND, add: ADD_SUBPLOT },
  chekhov_promise: { label: CHEKHOV_KIND, add: ADD_CHEKHOV },
  main: { label: MAIN_KIND, add: ADD_MAIN },
};

export default function PlotTrackAddRow({ rows, onAdd }) {
  const types = useMemo(() => addableRowTypes(rows), [rows]);
  const defaultType = types.includes('subplot') ? 'subplot' : types[0];
  const [open, setOpen] = useState(false);
  const [type, setType] = useState(defaultType);
  const [name, setName] = useState('');
  const [visibility, setVisibility] = useState('hidden');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const reset = () => {
    setOpen(false);
    setType(types.includes('subplot') ? 'subplot' : types[0]);
    setName('');
    setVisibility('hidden');
    setError(null);
  };

  const submit = async () => {
    const label = name.trim();
    if (type !== 'main' && !label) {
      setError(NAME_THIS_THROUGH_LINE);
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await onAdd({
        type,
        label: label || undefined,
        visibility: type === 'chekhov_promise' ? visibility : undefined,
      });
      reset();
    } catch (err) {
      setError(err?.message || 'Could not add that main storyline.');
    } finally {
      setBusy(false);
    }
  };

  if (!open) {
    return (
      <div className="pt-add-row">
        <button type="button" className="pt-add-row__open" onClick={() => setOpen(true)}>
          {ADD_THROUGH_LINE}
        </button>
      </div>
    );
  }

  return (
    <form
      className="pt-add-row pt-add-row--open"
      onSubmit={(event) => {
        event.preventDefault();
        submit();
      }}
    >
      <fieldset className="pt-add-row__types">
        <legend>{ADD_THROUGH_LINE}</legend>
        {types.map((value) => (
          <label key={value}>
            <input
              type="radio"
              name="pt-row-type"
              value={value}
              checked={type === value}
              onChange={() => setType(value)}
              aria-label={TYPE_COPY[value].label}
            />
            {TYPE_COPY[value].label}
          </label>
        ))}
      </fieldset>
      <label className="pt-add-row__field">
        {THROUGH_LINE_NAME}
        <input
          value={name}
          onChange={(event) => setName(event.target.value)}
          aria-label={NAME_THIS_THROUGH_LINE}
          placeholder={type === 'main' ? 'Main plot' : NAME_THIS_THROUGH_LINE}
        />
      </label>
      {type === 'chekhov_promise' && (
        <label className="pt-add-row__field">
          {CHEKHOV_HOW_CLEAR}
          <select
            value={visibility}
            onChange={(event) => setVisibility(event.target.value)}
            aria-label={CHEKHOV_HOW_CLEAR}
          >
            {CHEKHOV_VISIBILITIES.map((value) => {
              const copy = chekhovVisibilityCopy(value);
              return (
                <option key={value} value={value}>
                  {copy.label} — {copy.hint}
                </option>
              );
            })}
          </select>
        </label>
      )}
      {type === 'chekhov_promise' && (
        <p className="pt-add-row__hint">{CHEKHOV_FEEDS_SECRET}</p>
      )}
      {error && <p className="pt-add-row__error">{error}</p>}
      <div className="pt-add-row__actions">
        <button type="submit" disabled={busy}>{TYPE_COPY[type].add}</button>
        <button type="button" onClick={reset}>{CANCEL_THROUGH_LINE}</button>
      </div>
    </form>
  );
}
